import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Step from '@mui/material/Step';
import { connect } from 'react-redux';
import Modal from '@mui/material/Modal';
import { useDispatch } from 'react-redux';
import Divider from '@mui/material/Divider';
import { compose } from '@reduxjs/toolkit';
import { useParams } from 'react-router-dom';
import Stepper from '@mui/material/Stepper';
import { useNavigate } from 'react-router-dom';
import StepLabel from '@mui/material/StepLabel';
import Typography from '@mui/material/Typography';

import Spacer from 'core/spacer/Spacer';
import Button from 'core/buttons/Button';
import CircularProgress from 'core/progress/CircularProgress';

import { buildRoute } from 'routes';
import ucfirst from 'helpers/ucfirst';
import isEmpty from 'helpers/isEmpty';
import {
  retryToCreate,
  retryToUpdate,
  setShowEditProcessingModal,
  setShowCreateProcessingModal
} from 'app/features/partners/partnerSlice';

const style = {
  position: 'absolute',
  top: '64px',
  right: '0',
  width: 384,
  height: 'calc(100vh - 64px)',
  bgcolor: 'background.paper',
  border: 0,
  boxShadow: 24,
  p: 4
};

function getStep1Label(isAddMode) {
  return isAddMode ? 'Creating New Partner' : 'Updating partner Details';
}

function getStep2Label(isAddMode, isProcessed, isFailed) {
  if (isAddMode) {
    return isProcessed
      ? 'Partner Created successfully'
      : isFailed
      ? 'Failed to create partner'
      : '';
  }

  return isProcessed ? 'Partner Updated successfully' : isFailed ? 'Failed to update partner' : '';
}

function PartnerProgressIndicatorModal(props) {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const params = useParams();

  const { isAddMode, open, isProcessing, isProcessed, isFailed, partner, errorMessage } = props;

  const steps = useMemo(() => {
    return [
      {
        label: getStep1Label(isAddMode),
        showProgress: true,
        color: 'primary'
      },
      {
        label: getStep2Label(isAddMode, isProcessed, isFailed),
        showProgress: false,
        color: isProcessed ? 'primary' : 'error'
      }
    ];
  }, [isAddMode, isProcessed, isFailed]);

  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const hideProgressModal = () => {
    dispatch(isAddMode ? setShowCreateProcessingModal(false) : setShowEditProcessingModal(false));
  };

  useEffect(() => {
    if (isProcessing) handleNext();
  }, [isProcessing]);

  useEffect(() => {
    if (isProcessed) handleNext();
  }, [isProcessed]);

  return (
    <div>
      <Modal open={open} onClose={() => {}}>
        <Box sx={style}>
          <Box sx={{ maxWidth: 400 }}>
            {/* Step process */}

            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step) => (
                <Step key={step.label}>
                  <StepLabel color={step.color}>
                    <Box display="flex" gap={2} alignItems="center">
                      <Typography color={step.color}>{step.label}</Typography>
                      {isProcessing && step.showProgress && (
                        <CircularProgress size={30} thickness={6} />
                      )}
                    </Box>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
            <Spacer />

            {/* If Form Process Failed */}

            {isFailed && (
              <Box>
                <Typography variant="body2" color="error">
                  {ucfirst(errorMessage)}
                </Typography>
                <Spacer />
                <Button
                  color="primary"
                  onClick={() => dispatch(isAddMode ? retryToCreate() : retryToUpdate())}
                  fullWidth
                >
                  Retry
                </Button>
                <Spacer />
                <Button
                  color="error"
                  onClick={() => hideProgressModal()}
                  variant="outlined"
                  fullWidth
                >
                  Cancel
                </Button>
              </Box>
            )}

            {/* Is Form Process succeed */}
            {partner && (
              <Box display="flex" flexDirection="column" gap={1}>
                <Box>
                  <Typography variant="body1" color="black">
                    Partner Name:
                  </Typography>
                  <Typography variant="body2" color="grey.800">
                    {partner.name || '-'}
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="body1" color="black">
                    Contact Person Name:
                  </Typography>
                  <Typography variant="body2" color="grey.800">
                    {partner.cnt_name || '-'}
                  </Typography>
                </Box>
                <Divider />
                <Spacer />

                {isAddMode && (
                  <Box>
                    <Button
                      color="primary"
                      onClick={() => {
                        hideProgressModal();
                        navigate(
                          buildRoute('accounts.create') +
                            '?' +
                            new URLSearchParams({
                              partner_id: partner.id,
                              partner_name: partner.name
                            })
                        );
                      }}
                      fullWidth
                    >
                      SETUP LOGIN CREDENTIALS
                    </Button>
                  </Box>
                )}

                <Box>
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={() =>
                      navigate(buildRoute('partners.show', partner.id ?? params.partnerId))
                    }
                    fullWidth
                  >
                    VIEW DETAILS
                  </Button>
                </Box>
                {isEmpty(params?.partnerId) && (
                  <Button
                    color="primary"
                    onClick={() => hideProgressModal()}
                    variant="outlined"
                    fullWidth
                  >
                    Add New Partner
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Modal>
    </div>
  );
}

export default compose(connect(mapStateToProps))(PartnerProgressIndicatorModal);

function mapStateToProps(state, ownProps) {
  return {
    ...ownProps,
    open: ownProps.isAddMode
      ? state.partners.create.showProcessingModal
      : state.partners.update.showProcessingModal,
    isProcessing: ownProps.isAddMode
      ? state.partners.create.isCreating
      : state.partners.update.isUpdating,
    isProcessed: ownProps.isAddMode
      ? state.partners.create.isCreated
      : state.partners.update.isUpdated,
    isFailed: ownProps.isAddMode ? state.partners.create.isFailed : state.partners.update.isFailed,
    partner: ownProps.isAddMode ? state.partners.create.data : state.partners.update.data,
    errorMessage: ownProps.isAddMode
      ? state.partners.create.errorMessage
      : state.partners.update.errorMessage
  };
}

PartnerProgressIndicatorModal.propTypes = {
  isAddMode: PropTypes.bool.isRequired,
  open: PropTypes.bool,
  isProcessing: PropTypes.bool,
  isProcessed: PropTypes.bool,
  partner: PropTypes.object,
  isFailed: PropTypes.bool
};
